import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { nanoid } from "nanoid";
import { literalValueTypeNameValidator } from "../../primitives";
import { idValidator } from "../misc/id-validator";
import { createStateId, isStateId } from "./SWState";
// style_variable_group:[group-name] -> root, has group
// style_variable_group:[groupd-name].mode.[mode] -> Ex: light/dark, has order
// info
// style_variable_group:[group-name].variable.[variable] -> Ex: background,
// has order info & type info
// state:style.[group-name].[variable-name].[mode] -> Current value
export const styleVariableGroupValidator = T.model("style_variable_group", T.object({
    typeName: T.literal("style_variable_group"),
    id: idValidator("style_variable_group"),
    index: T.string,
    group: T.object({
        controlledBy: T.object({
            type: T.literal("state"),
            stateId: idValidator("state")
        })
    }).optional(),
    // Used to represent the type of the variable
    variableType: T.object({
        type: T.literal("value-type"),
        value: literalValueTypeNameValidator
    }).optional()
}));
export const SWStyleVariableGroupRecordType = createRecordType("style_variable_group", {
    migrations: defineMigrations({}),
    validator: styleVariableGroupValidator,
    scope: "document"
}).withDefaultProperties(()=>({}));
/** @public */ export function createStyleVariableGroupId(id) {
    return `style_variable_group:${id ?? nanoid()}`;
}
export function isStyleVariableGroup(record) {
    if (!record) return false;
    return record.typeName === "style_variable_group";
}
/** @public */ export function isStyleVariableGroupId(id) {
    return SWStyleVariableGroupRecordType.isId(id);
}
export const buildStyleVariableGroupIdHelper = (group)=>{
    return {
        root: ()=>createStyleVariableGroupId(group),
        startsWithVariablesQuery: ()=>createStyleVariableGroupId(`${group}.variable.`),
        startsWithModeQuery: ()=>createStyleVariableGroupId(`${group}.mode.`),
        variable: (name)=>createStyleVariableGroupId(`${group}.variable.${name}`),
        mode: (name)=>createStyleVariableGroupId(`${group}.mode.${name}`),
        state: (name)=>{
            return {
                mode: (mode)=>createStateId(`style.${group}.${name}.${mode}`)
            };
        }
    };
};
export const isStyleVariableGroupVariableId = (id)=>{
    if (!isStyleVariableGroupId(id)) return false;
    const parts = id.split(":")[1]?.split(".");
    // style_variable_group:[group-name].variable.[variable]
    if (parts.length === 3 && parts[1] === "variable") return true;
    return false;
};
export const isNodeVariableStateId = (id)=>{
    return isStateId(id) && id.replace("state:", "").startsWith("node.");
};
export const isStyleVariableStateId = (id)=>{
    return isStateId(id) && id.replace("state:", "").startsWith("style.");
};
export const isSelectedProductVariableStateId = (id)=>{
    return isStateId(id) && id.replace("state:", "").startsWith("products.selected.");
};
export const isProductsPeriodOrPeriodlyStateId = (id)=>{
    if (!isStateId(id)) return false;
    const parts = id.replace("state:", "").split(".");
    if (parts.length !== 3 || parts[0] !== "products") return false;
    return parts[2] === "period" || parts[2] === "periodly";
};
export const groupNameFromStateId = (id)=>{
    if (!isStateId(id)) return undefined;
    const parts = id.replace("state:", "").split(".");
    if (!parts[1] || parts[0] != "style") return undefined;
    return parts[1] // style.[group-name].[variable-name].[mode]
    ;
};
export const variableNameFromStateId = (id)=>{
    if (!isStateId(id)) return undefined;
    const parts = id.replace("state:", "").split(".");
    if (!parts[2] || parts[0] != "style") return undefined;
    return parts[2] // style.[group-name].[variable-name].[mode]
    ;
};
export const productVariableNameFromStateId = (id)=>{
    if (!isStateId(id)) return undefined;
    const parts = id.replace("state:", "").split(".");
    if (!parts[2] || parts[0] != "products") return undefined;
    return parts[2] // products.[product-name].[variable-name]
    ;
};
